import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { ValueTypes } from 'src/app/common/custom.types';
import FieldAllowedValueClasses from 'src/app/forms/fields/field-allowed-value-classes.values';
import {
  Attribute,
  CreateControlContent,
  FieldAttribute,
  FormConstructorContent,
  SelectorOption,
} from 'src/app/interfaces/form-and-field.interface';
import { isLocationDefined } from '../common/app.helpers';
import AppSettings from '../common/app.settings';
import { LocationObject, OrganizationType, OrganizationTypeKind } from '../swagger-api';
import FieldAllowedAttributeTypes from './fields/field-allowed-attribute-types.values';
import FieldsSettings from './fields/fields.settings';
import FormsHelpers from './forms.helpers';

export interface FormConstructorDataStrategy {
  createControls(content: CreateControlContent): void;

  getFormValues(form: UntypedFormGroup, formContent: FormConstructorContent): FormConstructorContent;
}

export class ModelData implements FormConstructorDataStrategy {
  private readonly incomingDataMapper: {
    [key: string]: (value: ValueTypes) => ValueTypes;
  } = {
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: (value: ValueTypes) =>
      this.convertOrganizationTypeToSelectorOption(value),
    [FieldAllowedValueClasses.GENDER_SELECT]: (value: ValueTypes) =>
      value ? { entity_id: value, title: value } : null,
    [FieldAllowedValueClasses.LOCATION]: (value: ValueTypes) =>
      value && !isLocationDefined(value as LocationObject) ? value : null,
    [FieldAllowedValueClasses.CITY_LEVEL_LOCATION]: (value: ValueTypes) =>
      value && !isLocationDefined(value as LocationObject) ? value : null,
    [FieldAllowedValueClasses.LOCATION_ALL_TYPES]: (value: ValueTypes) =>
      value && !isLocationDefined(value as LocationObject) ? value : null,
  };

  private readonly outgoingDataMapper: {
    [key: string]: (value: ValueTypes) => ValueTypes;
  } = {
    [FieldAllowedValueClasses.LINKED_ORGANIZATION_IDS_SELECT]: (value: ValueTypes) => value || [],
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: (value: ValueTypes) =>
      this.convertSelectorOptionToOrganizationType(value as SelectorOption),
    [FieldAllowedValueClasses.GENDER_SELECT]: (value: ValueTypes) => (value ? (value as SelectorOption).entity_id : ''),
    [FieldAllowedValueClasses.COMMUNITIES_SEARCH_SELECT]: (value: ValueTypes) =>
      value ? (value as SelectorOption).entity_id : '',
    [FieldAllowedValueClasses.CONNECT_COMMUNITY_SEARCH_SELECT]: (value: ValueTypes) =>
      value ? (value as SelectorOption).entity_id : '',
    [FieldAllowedValueClasses.PROJECTS_SEARCH_SELECT]: (value: ValueTypes) =>
      value ? (value as SelectorOption).entity_id : '',
    [FieldAllowedValueClasses.MAP_SPECIALTIES_SELECT]: (value: ValueTypes): object[] => {
      return value ? (value as SelectorOption[]) : [];
    },
    [FieldAllowedValueClasses.MAP_DISEASES_SELECT]: (value: ValueTypes): object[] => {
      return value ? (value as SelectorOption[]) : [];
    },
    [FieldAllowedValueClasses.MAP_CHARITY_FOCUSES_SELECT]: (value: ValueTypes): object[] => {
      return value ? (value as SelectorOption[]) : [];
    },
    [FieldAllowedValueClasses.SPECIALTIES_SELECT]: (value: ValueTypes): string[] => {
      return value ? (value as SelectorOption[]).map((s) => s.entity_id) : [];
    },
    [FieldAllowedValueClasses.DISEASES_SELECT]: (value: ValueTypes): string[] => {
      return value ? (value as SelectorOption[]).map((d) => d.entity_id) : [];
    },
    [FieldAllowedValueClasses.CHARITY_FOCUSES]: (value: ValueTypes): string[] => {
      return value ? (value as SelectorOption[]).map((c) => c.entity_id) : [];
    },
  };

  private readonly prefixMapper: { [key: string]: string } = {
    [FieldAllowedValueClasses.LANGUAGES_SELECT]: '',
    [FieldAllowedValueClasses.ROLES_SELECT]: FieldsSettings.INDIVIDUAL_ROLE_PREFIX,
    [FieldAllowedValueClasses.MAP_ROLES_SELECT]: FieldsSettings.INDIVIDUAL_ROLE_PREFIX,
  };

  public createControls(content: CreateControlContent): void {
    content.formContent.fieldAttributes.forEach((field: FieldAttribute) => {
      const value = content.formContent.data[field.backendAttribute.name] as ValueTypes;

      addField(content, field, this.initModelDataValue(field.backendAttribute, value));
    });
  }

  public getFormValues(form: UntypedFormGroup, formContent: FormConstructorContent): FormConstructorContent {
    this.formateModelData(form, formContent);
    return formContent;
  }

  private initModelDataValue(attr: Attribute, value: ValueTypes): ValueTypes {
    if (attr.name == 'specialties' || attr.name == 'diseases') {
      return value;
    }

    if (this.isNotEmptyMultipleSearchSelect(value, attr)) {
      const prefix: string = this.prefixMapper[attr.value_class_front];
      return (value as []).map((speciality: string) => ({
        entity_id: speciality,
        title: `${prefix}${speciality}`,
      }));
    }

    if (this.incomingDataMapper[attr.value_class_front]) {
      return this.incomingDataMapper[attr.value_class_front](value);
    }

    return value;
  }

  /**
   * @description Converts the incoming organizationType object to expected by forms
   *              constructor SelectorOption model. Has three resolvers that used depend
   *              on object initial value.
   * @param { ValueTypes } value
   * @returns { SelectorOption | null }
   */
  private convertOrganizationTypeToSelectorOption(value: ValueTypes): SelectorOption | null {
    const organizationType: OrganizationType = value as OrganizationType;
    if (!organizationType) {
      return null;
    }

    if (organizationType.type === OrganizationTypeKind.Custom) {
      return organizationType.custom_organization_type_name
        ? {
            entity_id: organizationType.type,
            title: organizationType.custom_organization_type_name,
          }
        : null;
    }

    return {
      entity_id: organizationType.type,
      title: `${FieldsSettings.ORGANIZATION_TYPE_PREFIX}${organizationType.type}`,
    };
  }

  /**
   * @description Converts the outgoing object with type of SelectorOption to expected by
   *              service OrganizationType model.
   * @param { SelectorOption } value
   * @returns { OrganizationType }
   */
  private convertSelectorOptionToOrganizationType(value: SelectorOption): OrganizationType {
    const type: string = value.entity_id.toLowerCase().replace(/ /gi, '_');
    if (FormsHelpers.notCustomOrganizationTypes.includes(type)) {
      return { type: type as OrganizationTypeKind };
    }

    return {
      type: OrganizationTypeKind.Custom,
      custom_organization_type_name: value.title,
    };
  }

  private formateModelData(form: UntypedFormGroup, formContent: FormConstructorContent): void {
    const keys: string[] = Object.keys(form.controls);
    formContent.data = {};
    keys.forEach((key: string) => {
      const field: FieldAttribute = formContent.fieldAttributes.find(
        (item: FieldAttribute) => item.backendAttribute.name === key,
      );

      if (!field) {
        formContent.data[key] = '';
        return;
      }

      if (!(form.controls[key].disabled && !form.controls[key].value)) {
        formContent.data[key] = this.getFieldValue(form, field, key);
      }
    });
  }

  private getFieldValue(form: UntypedFormGroup, field: FieldAttribute, key: string): ValueTypes {
    if (this.outgoingDataMapper[field.backendAttribute.value_class_front]) {
      return this.outgoingDataMapper[field.backendAttribute.value_class_front](form.controls[key].value as string);
    }

    if (this.isArrayValueField(field.backendAttribute)) {
      return form.controls[key].value
        ? (form.controls[key].value as string).split(AppSettings.FORM_FIELD_ARRAY_VALUE_SEPARATOR)
        : [];
    }

    return form.controls[key].value as ValueTypes;
  }

  private isMultipleSearchSelect(attr: Attribute): boolean {
    return attr.type_attribute === FieldAllowedAttributeTypes.MULTIPLE_SEARCH_SELECT;
  }

  private isNotEmptyMultipleSearchSelect(value: ValueTypes, attr: Attribute): boolean {
    return Boolean(value) && this.isMultipleSearchSelect(attr);
  }

  private isArrayValueField(attribute: Attribute): boolean {
    return this.isLinkedIdsSelect(attribute) || this.isMultipleSearchSelect(attribute);
  }

  private isLinkedIdsSelect(attribute: Attribute): boolean {
    return attribute.type_attribute === FieldAllowedAttributeTypes.MULTIPLE_SUGGESTIONS_SEARCH_SELECT;
  }
}

function addField(content: CreateControlContent, field: FieldAttribute, value: ValueTypes = ''): void {
  const control: UntypedFormControl = content.fieldBuilder.control(
    { value, disabled: field.backendAttribute.read_only },
    field.validators,
  );
  content.form.addControl(field.backendAttribute.name, control);
}
